import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Loader2, Pencil, Search, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { translateFr as tfr } from "@/i18n";
import { toast } from "sonner";
import { FounderOverview } from "@/components/admin/FounderOverview";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const GB = 1024 ** 3;
const PLAN_NAMES = { starter: "Starter", pro: "Pro", agency: "Agency", enterprise: "Enterprise" };
const STATUSES = ["trialing", "active", "past_due", "canceled", "expired"];

function EditDialog({ account, onClose, onSaved }) {
  const [plan, setPlan] = useState(account.plan_id);
  const [status, setStatus] = useState(account.subscription_status || "trialing");
  const [players, setPlayers] = useState(account.custom_limits?.players ?? "");
  const [users, setUsers] = useState(account.custom_limits?.users ?? "");
  const [storageGb, setStorageGb] = useState(
    account.custom_limits?.storage_bytes != null ? Math.round(account.custom_limits.storage_bytes / GB) : "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    const custom_limits = {};
    if (players !== "") custom_limits.players = parseInt(players, 10);
    if (users !== "") custom_limits.users = parseInt(users, 10);
    if (storageGb !== "") custom_limits.storage_bytes = parseInt(storageGb, 10) * GB;
    try {
      await api.post(`/admin/accounts/${account.user_id}`, {
        plan_id: plan, subscription_status: status, custom_limits, note,
      });
      toast.success("Compte mis à jour");
      onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erreur");
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Administration — {account.email}</DialogTitle>
          <DialogDescription>{tfr("Plan, statut et quotas personnalisés (Enterprise).")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Offre</label>
              <Select value={plan} onValueChange={setPlan}>
                <SelectTrigger data-testid="admin-plan-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PLAN_NAMES).map(([id, n]) => <SelectItem key={id} value={id}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Statut</label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="admin-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Joueurs</label>
              <Input type="number" min="0" value={players} placeholder="plan"
                onChange={(e) => setPlayers(e.target.value)} data-testid="admin-limit-players" />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">{tfr("Sièges")}</label>
              <Input type="number" min="0" value={users} placeholder="plan"
                onChange={(e) => setUsers(e.target.value)} data-testid="admin-limit-users" />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Stockage (Go)</label>
              <Input type="number" min="0" value={storageGb} placeholder="plan"
                onChange={(e) => setStorageGb(e.target.value)} data-testid="admin-limit-storage" />
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">{tfr("Laisser vide = limites du plan. Les quotas personnalisés priment sur le plan.")}</p>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note interne (audit)"
            data-testid="admin-note-input" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button onClick={save} disabled={busy} data-testid="admin-save-btn">
            {busy && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminConsole() {
  const { user, loading } = useAuth();
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async (query = "") => {
    try {
      const r = await api.get(`/admin/accounts${query ? `?q=${encodeURIComponent(query)}` : ""}`);
      setRows(r.data.accounts);
    } catch (e) { console.error("admin", e); setRows([]); }
  }, []);
  useEffect(() => { if (user?.is_staff) load(); }, [user, load]);

  if (loading) return null;
  if (!user?.is_staff) return <Navigate to="/dashboard" replace />;

  return (
    <div className="w-full max-w-[1180px] mx-auto px-4 sm:px-6 pt-7 pb-16 space-y-6" data-testid="admin-console">
      <header className="flex items-center gap-3">
        <ShieldCheck className="h-6 w-6 text-athlynx" />
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-[-0.02em]">Console Enterprise</h1>
          <p className="text-sm text-muted-foreground">{tfr("Outil interne — plans, quotas personnalisés et statuts.")}</p>
        </div>
      </header>

      <FounderOverview />

      <div className="flex gap-2 max-w-md">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Rechercher par email, nom, ID…" value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load(q)}
            data-testid="admin-search-input" />
        </div>
        <Button variant="secondary" onClick={() => load(q)} data-testid="admin-search-btn">Rechercher</Button>
      </div>

      {!rows ? (
        <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : (
        <div className="rounded-2xl border border-border overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-accounts-table">
            <thead>
              <tr className="border-b border-border text-left text-xs text-muted-foreground">
                <th className="px-4 py-3 font-medium">Compte</th>
                <th className="px-4 py-3 font-medium">Offre</th>
                <th className="px-4 py-3 font-medium">Statut</th>
                <th className="px-4 py-3 font-medium">Joueurs</th>
                <th className="px-4 py-3 font-medium">{tfr("Sièges")}</th>
                <th className="px-4 py-3 font-medium">Stockage</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => {
                const lim = a.effective_limits || {};
                const hasCustom = a.custom_limits && Object.keys(a.custom_limits).length > 0;
                return (
                  <tr key={a.user_id} className="border-b border-border/50 hover:bg-secondary/30">
                    <td className="px-4 py-3">
                      <div className="font-medium">{a.name || "—"}</div>
                      <div className="text-xs text-muted-foreground">{a.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      {PLAN_NAMES[a.plan_id] || a.plan_id}
                      {hasCustom && <span className="ml-1.5 text-[10px] uppercase text-athlynx font-semibold">custom</span>}
                    </td>
                    <td className="px-4 py-3 text-xs">{a.subscription_status || "—"}</td>
                    <td className="px-4 py-3 text-xs">{a.usage?.players ?? 0} / {lim.players ?? "∞"}</td>
                    <td className="px-4 py-3 text-xs">{a.usage?.members ?? 1} / {lim.users ?? "∞"}</td>
                    <td className="px-4 py-3 text-xs">
                      {((a.usage?.storage_bytes || 0) / GB).toFixed(2)} / {lim.storage_bytes != null ? `${Math.round(lim.storage_bytes / GB)} Go` : "∞"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEditing(a)}
                        data-testid={`admin-edit-${a.user_id}`} aria-label="Modifier">
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <EditDialog account={editing} onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(q); }} />
      )}
    </div>
  );
}
